import React, { useState, useEffect, useRef } from 'react';
import * as PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core';
import { IdPropType } from '../../../proptypes/basic';
import MailView from './MailView';
import InboxList from './InboxList';
import { useUserEmployee, userHasUserEmployeeEmailAccess } from '../../auth/hooks';
import { useEmail } from '../emailsSlice';
import { useSnackbar } from '../../snackbar/hooks';

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100%',
    },

    list: {
        maxWidth: 450,
        height: '100%',
        overflowY: 'auto',
        borderRight: `1px solid ${theme.palette.grey[300]}`,
    },

    content: {
        overflowX: 'auto',
        height: '100%',
    },
}));

const createListId = (customerId = null) => {
    if (customerId) {
        return `emails.${customerId}.list`;
    }
    return 'emails.list';
};

const MailInbox = ({
    customerId,
    mailId,
    hideFolderSelect,
    selectedFolder,
    onChangeFolder,
}) => {
    const classes = useStyles();
    const routeMailId = mailId ? Number(mailId) : null;
    const { employee: userEmployee } = useUserEmployee();
    const [mail] = useEmail(routeMailId);
    const hasAccess = userHasUserEmployeeEmailAccess(userEmployee, mail?.emailInboxId);
    const [selected, setSelected] = useState(null);
    const appliedRouteMailId = useRef(null);
    //console.log('MailInbox userEmployee =>', userEmployee?.emailInboxes, mail?.emailInboxId, hasAccess);
    const { enqueueSnackbar , closeSnackbar} = useSnackbar();

    // If mailId changes (e.g. via route), select it once the mail and access data are available.
    useEffect(() => {
        if (!routeMailId) {
            appliedRouteMailId.current = null;
            return;
        }

        if (!mail || !userEmployee) {
            return;
        }

        if (appliedRouteMailId.current === routeMailId) {
            return;
        }

        appliedRouteMailId.current = routeMailId;

        if (!hasAccess) {
            //console.log('MailInbox: User does not have access to this email inbox:', mail?.emailInboxId);
            if (selected !== null) {
                setSelected(null);
            }

            // Show a snackbar with the error message
            let errorMessage = `Kein Zugriff auf angeforderteE-Mail! Postfach: ${mail?.emailInboxId}! => Zeige Default-Postfach`;
            enqueueSnackbar(
                errorMessage,
                {
                    key: 'no-access-inbox',
                    variant: 'error',
                    persist: true,
                    style: { whiteSpace: 'pre-line' },
                    preventDuplicate: true
                }
            );
            return;
        }

        closeSnackbar('no-access-inbox');
        if (selected !== routeMailId) {
            setSelected(routeMailId);
        }
    }, [
        routeMailId,
        selected,
        mail,
        userEmployee,
        hasAccess,
        closeSnackbar,
        enqueueSnackbar
    ]);

    //console.log('MailInbox selected =>', selected);

    return (
        <Grid container className={classes.root}>
            <Grid item xs={4} className={classes.list}>
                <InboxList
                    listId={createListId(customerId)}
                    customerId={customerId}
                    selected={selected}
                    onSelect={setSelected}
                    hideFolderSelect={hideFolderSelect}
                    selectedFolder={selectedFolder}
                    onChangeFolder={onChangeFolder}
                />
            </Grid>
            <Grid item xs className={classes.content}>
                <MailView mailId={selected} popup={true} />
            </Grid>
        </Grid>
    );
};

MailInbox.propTypes = {
    customerId: IdPropType,
    mailId: IdPropType,
    key: IdPropType,
    hideFolderSelect: PropTypes.bool,
    selectedFolder: PropTypes.string.isRequired,
    onChangeFolder: PropTypes.func.isRequired,
};

MailInbox.defaultProps = {
    customerId: null,
    mailId: null,
    key: null,
    hideFolderSelect: false,
};

export default MailInbox;
